<template>
  <section class="dir-layout">
    <!-- Sidebar (desktop only) -->
    <DirectorySidebar
      :activeTab="activeTabName"
      :activeFilter="activeFilter"
      :businessCount="businessCount"
      :contactsCount="contactsCount"
      :carriersCount="carriersCount"
      @change-tab="onChangeTab"
      @change-filter="onChangeFilter"
    />

    <!-- Main content -->
    <div class="dir-content">
      <TopBarTitleComponent title="Directorio" :showBackIcon="true">
        <template slot="right-area">
          <div class="d-flex justify-content-end align-items-center">
            <ButtonPrimaryComponent
              v-if="activeTabName === 'business' && $can('clients.manage')"
              title="Nueva Empresa"
              @onSaveClick="goRouter('business.add')"
            />
            <ButtonPrimaryComponent
              v-else-if="activeTabName === 'contacts' && $can('clients.manage')"
              title="Nuevo Contacto"
              @onSaveClick="onAddContact"
            />
            <ButtonPrimaryComponent
              v-else-if="
                activeTabName === 'carriers' &&
                  $can('clients.manage', 'remissions.manage')
              "
              title="Nuevo Transportista"
              @onSaveClick="onAddCarrier"
            />
          </div>
        </template>
      </TopBarTitleComponent>

      <div class="dir-content-body">
        <!-- Header + counts -->
        <div class="dir-header">
          <div>
            <h3 class="dir-header-title d-lg-none">Directorio</h3>
            <p class="dir-header-subtitle d-md-none">
              {{ businessCount }} empresas · {{ contactsCount }} contactos
            </p>
          </div>
        </div>

        <!-- Pill tabs (mobile only: replaces sidebar) -->
        <div class="dir-tabs d-lg-none">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            class="dir-tab"
            :class="{ active: activeTabName === tab.key }"
            @click="onChangeTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- Tab content -->
        <!-- v-if = lazy first mount, v-show = keep alive after (no reload on tab switch) -->
        <div v-if="mountedTabs.business" v-show="activeTabName === 'business'">
          <ListBusinessComponent
            :filterType="activeFilter"
            @update-count="onBusinessCountUpdate"
          />
        </div>
        <div v-if="mountedTabs.contacts" v-show="activeTabName === 'contacts'">
          <ListBusinessContactsComponent
            ref="contactsList"
            @update-count="onContactsCountUpdate"
          />
        </div>
        <div v-if="mountedTabs.carriers" v-show="activeTabName === 'carriers'">
          <ListCarriersComponent
            ref="carriersList"
            @update-count="onCarriersCountUpdate"
          />
        </div>
        <div
          v-if="mountedTabs.notifications"
          v-show="activeTabName === 'notifications'"
        >
          <NotificationListComponent />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import TopBarTitleComponent from "../components/TopBarTitle";
import ListBusinessComponent from "./components/List";
import ListBusinessContactsComponent from "./components/ListContacts";
import ListCarriersComponent from "./components/ListCarriers";
import NotificationListComponent from "./components/notifications/NotificationList";
import ButtonPrimaryComponent from "../components/ButtonPrimary";
import DirectorySidebar from "./components/DirectorySidebar";
import businessService from "./services/businessService";

export default {
  name: "MainBusiness",
  components: {
    TopBarTitleComponent,
    ListBusinessComponent,
    ListBusinessContactsComponent,
    ListCarriersComponent,
    NotificationListComponent,
    ButtonPrimaryComponent,
    DirectorySidebar
  },
  data: () => ({
    activeTabName: "business",
    activeFilter: "",
    businessCount: 0,
    contactsCount: 0,
    carriersCount: 0,
    // Tracks which tabs have been opened at least once.
    // v-if mounts lazily on first visit; v-show keeps them alive after.
    mountedTabs: {
      business: true,
      contacts: false,
      carriers: false,
      notifications: false
    }
  }),
  computed: {
    tabs() {
      return [
        { key: "business", label: "Empresas" },
        { key: "contacts", label: "Contactos" },
        { key: "carriers", label: "Transportistas" },
        { key: "notifications", label: "Notificaciones" }
      ];
    }
  },
  methods: {
    goRouter(name, params = {}) {
      this.$router.push({ name, params });
    },
    onChangeTab(tab) {
      this.activeTabName = tab;
      this.activeFilter = "";
      this.$set(this.mountedTabs, tab, true);
    },
    onChangeFilter(filter) {
      this.activeFilter = this.activeFilter === filter ? "" : filter;
      if (this.activeTabName !== "business") {
        this.activeTabName = "business";
      }
    },
    onAddContact() {
      if (this.$refs.contactsList) {
        this.$refs.contactsList.openAddDialog();
      }
    },
    onBusinessCountUpdate(count) {
      this.businessCount = count;
    },
    onContactsCountUpdate(count) {
      this.contactsCount = count;
    },
    onCarriersCountUpdate(count) {
      this.carriersCount = count;
    },
    onAddCarrier() {
      if (this.$refs.carriersList) {
        this.$refs.carriersList.openAddDialog();
      }
    },
    async fetchContactsCount() {
      try {
        let response = await businessService.getContacts({
          params: { per_page: 1 }
        });
        if (response.data) {
          const { pagination, contacts } = response.data;
          if (pagination && pagination.total != null) {
            this.contactsCount = pagination.total;
          } else if (contacts) {
            this.contactsCount = contacts.length;
          }
        }
      } catch (e) {
        // Silently fail
      }
    },
    async fetchCarriersCount() {
      try {
        let response = await businessService.getCarriers({
          params: { per_page: 1 }
        });
        if (response.data) {
          const { pagination, carriers } = response.data;
          if (pagination && pagination.total != null) {
            this.carriersCount = pagination.total;
          } else if (carriers) {
            this.carriersCount = carriers.length;
          }
        }
      } catch (e) {
        // Silently fail
      }
    }
  },
  mounted() {
    this.fetchContactsCount();
    this.fetchCarriersCount();
  }
};
</script>

<style lang="scss" scoped></style>
